import _ from 'underscore';
import { useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import { usePromisify } from '@bingads-webui-react/overreact';
import {
  useGetCouponDetails,
  useGetCouponValidForCustomerWebsite,
  useGetCouponStringForSignup,
  useSaveCouponStringForSignup,
} from '@bingads-webui/overreact-cc';
import { useCustomerLevelVariables, usePostponeReadVariables } from '@bingads-webui-campaign-react/basic-context-utils';

import {
  actions, initialState, usePromotionalOfferReducer,
} from './use-promotional-offer-reducer';
import { useUrlCoupon } from './use-url-coupon';
import { WebsiteValidationStatus, DEFAULT_ACTIVITY } from '../helpers';

// Hook to fetch and resolve a coupon.
// UsePromotionalOffer, on initialize triggers following flow,
// 1. Fetch of couponValue (from context, URL or store)
// 2. Fetch coupon details based on the coupon value.
// 3. Fetch dialog coupon details if needed.
// 4. If the fetched coupon is Acquisition coupon, check for website eligible.
// 5. Save the coupon value to the store.
// 6. Allow setting manual coupon id and triggering process 2 to 5 above

export const usePromotionalOffer = ({
  activity = DEFAULT_ACTIVITY,
  backupCoupon,
  website,
  isSignUp = false,
  isSkipWebsiteValidation = false,
}) => {
  const customerLevelVariables = useCustomerLevelVariables();
  const postponeReadConfig = usePostponeReadVariables();

  const [, { read: getCouponDetails }] = useGetCouponDetails(customerLevelVariables, postponeReadConfig);
  const [, { read: validateWebsite }] = useGetCouponValidForCustomerWebsite(customerLevelVariables, postponeReadConfig);
  const [, { read: getCouponValue }] = useGetCouponStringForSignup(customerLevelVariables, postponeReadConfig);
  const [, { update: saveCouponValue }] = useSaveCouponStringForSignup(customerLevelVariables, postponeReadConfig);

  const [getCouponDetailsAsync, validateWebsiteAsync, getCouponValueAsync, saveCouponValueAsync]
    = usePromisify([getCouponDetails, validateWebsite, getCouponValue, saveCouponValue]);

  const [isCouponDetailsLoading, setIsCouponDetailsLoading] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [isCouponValueLoading, setIsCouponValueLoading] = useState(false);
  const [isCouponValueSaving, setIsCouponValueSaving] = useState(false);

  const { urlCoupon, isValidUrlCoupon } = useUrlCoupon(activity);

  const { backupCouponClass, backupCouponDetailsConstant } = backupCoupon || {};
  const backupCouponValue = useMemo(() => {
    if (!backupCouponClass) {
      return null;
    }
    return ({
      couponString: backupCouponClass,
      couponStringType: 'ccl',
    });
  }, [backupCouponClass]);

  const canSetDialogCoupon = useMemo(() => (
    isSignUp
  ), [isSignUp]);

  // Init reducer
  const [state, dispatch] = useReducer(usePromotionalOfferReducer, initialState);

  const requestInProgressDispatch = useCallback(() => dispatch({
    type: actions.REQUEST_IN_PROGRESS,
  }), []);

  const couponValueDispatch = useCallback(value => dispatch({
    type: actions.FETCHED_USER_COUPON_VALUE,
    couponValue: value,
    dialogCouponValue: canSetDialogCoupon ? backupCouponValue : null, // default value
  }), [backupCouponValue, canSetDialogCoupon]);

  const websiteValidationDispatch = useCallback(value => dispatch({
    type: actions.VALIDATED_WEBSITE,
    websiteValidationResult: value,
  }), []);

  const couponDetailsDispatch = useCallback((details, value) => dispatch({
    type: actions.SET_COUPON_DETAILS,
    couponDetails: details,
    couponValue: value,
    dialogCouponValue: canSetDialogCoupon ? backupCouponValue : null, // default value
    isSkipWebsiteValidation,
  }), [backupCouponValue, canSetDialogCoupon, isSkipWebsiteValidation]);

  const dialogCouponDetailsDispatch = useCallback(dialogDetails => dispatch({
    type: actions.SET_DIALOG_COUPON_DETAILS,
    dialogCouponDetails: dialogDetails,
  }), []);

  // Init coupon value. The value will be set based on the below preference order
  // 1. (resume scenarios) get coupon id stored in user details store
  // 2. Paymentcontext (tracked by ccui but only for the session)
  // 3. coupon code/class in url
  // If we find coupon from store as well as URL, we need to popup the
  // multi-coupon selection
  useEffect(() => {
    if (!state.canGetUserCouponValue || isCouponValueLoading || state.isRequestInProgress) {
      return;
    }
    // try getting from saved user preference
    activity.trace('Getting coupon from user info store');
    requestInProgressDispatch();
    setIsCouponValueLoading(true);
    getCouponValueAsync({})
      .then((result) => {
        let userCoupon = null;

        if (result.length > 0 && result[0].CouponString) {
          userCoupon = {
            couponString: result[0].CouponString,
            couponStringType: result[0].CouponStringType,
          };

          // Check if multi coupons detected and set saved coupon as primary
          if (isValidUrlCoupon && (userCoupon.couponString !== urlCoupon.couponString)) {
            activity.trace(`Found Multi coupons url: ${urlCoupon.couponString}, type ${urlCoupon.couponStringType}; saved: ${userCoupon.couponString}, type ${userCoupon.couponStringType}`);
          } else {
            activity.trace(`Found user coupon in store: string: ${result[0].couponString}, type: ${result[0].couponStringType}`);
          }
          couponValueDispatch(userCoupon);
        } else if (isValidUrlCoupon) {
          // Use the url coupon as there is no saved coupon
          userCoupon = urlCoupon;
        }

        couponValueDispatch(userCoupon);
      })
      .catch(() => {
        couponValueDispatch(null);
      })
      .finally(() => {
        setIsCouponValueLoading(false);
      });
  }, [isCouponValueLoading,
    state.canGetUserCouponValue,
    state.isRequestInProgress,
    getCouponValueAsync,
    couponValueDispatch,
    requestInProgressDispatch,
    activity,
    urlCoupon,
    isValidUrlCoupon]);

  // Initialize website eligibility
  useEffect(() => {
    if (!website ||
      isValidating ||
      state.isRequestInProgress ||
      !state.canValidateWebsite ||
      isSkipWebsiteValidation) {
      return;
    }
    requestInProgressDispatch();
    activity.trace(`Validating website. websiteL: ${_.size(website)}`);
    // Check website validity
    setIsValidating(true);
    validateWebsiteAsync({ variables: { websiteUrl: website } })
      .then((response) => {
        activity.trace(`Website validation for ${website} got ${response[0].IsValid}`);
        websiteValidationDispatch(response[0].IsValid);
      })
      .catch((error) => {
        activity.trace(`Website validation for ${website} failed. error: ${JSON.stringify(error)}`);
        websiteValidationDispatch(WebsiteValidationStatus.MTError);
      })
      .finally(() => {
        setIsValidating(false);
      });
  }, [
    isSkipWebsiteValidation,
    isValidating,
    state.canValidateWebsite,
    state.isRequestInProgress,
    website,
    websiteValidationDispatch,
    requestInProgressDispatch,
    validateWebsiteAsync,
    activity,
  ]);

  useEffect(() => {
    if (isCouponDetailsLoading || state.isRequestInProgress || !state.canGetCouponDetails) {
      return;
    }

    const couponParams = (state.couponValue.couponStringType === 'cco') ?
      { cco: state.couponValue.couponString } :
      { ccl: state.couponValue.couponString };

    requestInProgressDispatch();
    activity.trace(`Getting details for coupon: ${state.couponValue.couponString}`);
    setIsCouponDetailsLoading(true);
    getCouponDetailsAsync({ variables: couponParams })
      .then((result) => {
        if (_.isArray(result) && result.length > 0 && result[0].CouponDetails) {
          couponDetailsDispatch(result[0].CouponDetails, state.couponValue);
        } else {
          // If coupon failed to return details, we will not store the coupon
          // however we still need to show the user the coupon value with error message.
          couponDetailsDispatch(null, state.couponValue);
          activity.trace(`Coupon details not found for coupon: ${state.couponValue.couponString}, type: ${state.couponValue.couponStringType}`);
        }
      })
      .catch(() => {
        couponDetailsDispatch(null, {});
      })
      .finally(() => {
        setIsCouponDetailsLoading(false);
      });
  }, [
    state.couponValue,
    state.canGetCouponDetails,
    state.isRequestInProgress,
    isCouponDetailsLoading,
    getCouponDetailsAsync,
    couponDetailsDispatch,
    requestInProgressDispatch,
    activity,
  ]);

  useEffect(() => {
    if (isCouponDetailsLoading || state.isRequestInProgress || !state.canGetBackupCouponDetails) {
      return;
    }

    const couponParams = { ccl: state.dialogCouponValue.couponString };

    requestInProgressDispatch();
    activity.trace(`Getting details for backup coupon: ${state.dialogCouponValue.couponString}`);
    setIsCouponDetailsLoading(true);
    getCouponDetailsAsync({ variables: couponParams })
      .then((result) => {
        if (_.isArray(result) && result.length > 0 && result[0].CouponDetails) {
          dialogCouponDetailsDispatch(result[0].CouponDetails);
        } else {
          // If backup coupon failed to return details, will use the hard code detail
          dialogCouponDetailsDispatch(backupCouponDetailsConstant);
          activity.trace(`Coupon details not found for backup coupon: ${state.dialogCouponValue.couponString}, type: ${state.dialogCouponValue.couponStringType}`);
        }
      })
      .catch(() => {
        dialogCouponDetailsDispatch(backupCouponDetailsConstant);
      })
      .finally(() => {
        setIsCouponDetailsLoading(false);
      });
  }, [
    state.dialogCouponValue,
    state.canGetBackupCouponDetails,
    state.isRequestInProgress,
    isCouponDetailsLoading,
    getCouponDetailsAsync,
    dialogCouponDetailsDispatch,
    backupCouponDetailsConstant,
    requestInProgressDispatch,
    activity,
  ]);

  useEffect(() => {
    if (isCouponValueSaving || state.isRequestInProgress || !state.canSaveUserCouponValue) {
      return;
    }
    const value = (state.couponValue &&
      state.couponValue.couponString &&
      state.couponDetails &&
      !state.isCouponExpired) ?
      state.couponValue :
      { couponString: '', couponStringType: 'cco' };

    requestInProgressDispatch();

    activity.trace(`Saving coupon to user store: ${value.couponString || 'null'}`);

    setIsCouponValueSaving(true);
    saveCouponValueAsync({
      data: value,
    }).catch((e) => {
      activity.trace(`Saving coupon error: ${JSON.stringify(e)}`);
    }).finally(() => {
      dispatch({ type: actions.STORED_USER_COUPON_VALUE });
      setIsCouponValueSaving(false);
    });
  }, [state.isRequestInProgress,
    state.canSaveUserCouponValue,
    state.couponValue,
    state.couponDetails,
    saveCouponValueAsync,
    isCouponValueSaving,
    requestInProgressDispatch,
    activity,
    state.isCouponExpired]);

  useEffect(() => {
    if (state.isRequestInProgress || !state.canRunUpdatedCouponCallback) {
      return;
    }
    state.updatedCouponCallback();
    dispatch({ type: actions.RESET_UPDATED_COUPON_CALLBACK });
  }, [
    state,
    dispatch,
  ]);

  const initializePromotionalOffers = useCallback(() => {
    dispatch({
      type: actions.INITIALIZE,
      shouldInitialize: true,
    });
  }, []);

  const updateUserCoupon = useCallback(({
    couponString,
    couponStringType = 'cco',
    isManualCouponCode = true,
    updatedCouponCallback = null,
  }) => {
    const userCoupon = {
      couponString,
      couponStringType,
    };

    activity.trace(`Setting user coupon: type: ${couponStringType}, string: ${couponString}`);
    dispatch({
      type: actions.UPDATE_USER_COUPON_VALUE,
      couponValue: userCoupon,
      isManualCouponCode,
      updatedCouponCallback,
    });
  }, [activity]);

  const resetCouponStore = useCallback(() => {
    const value = { couponString: '', couponStringType: 'cco' };
    setIsCouponValueSaving(true);
    return saveCouponValueAsync({
      data: value,
    }).catch((e) => {
      activity.trace(`Resetting coupon error: ${JSON.stringify(e)}`);
    }).finally(() => {
      dispatch({ type: actions.STORED_USER_COUPON_VALUE, value });
      setIsCouponValueSaving(false);
    });
  }, [saveCouponValueAsync, activity]);

  const isLoadingCoupon = useMemo(() => (
    isValidating || isCouponDetailsLoading || isCouponValueLoading ||
    state.isRequestInProgress || state.canGetCouponDetails
  ), [isCouponDetailsLoading,
    isCouponValueLoading,
    isValidating,
    state.canGetCouponDetails,
    state.isRequestInProgress]);

  const coupon = useMemo(() => ({
    isLoading: isLoadingCoupon,
    websiteValidationResult: state.websiteValidationResult,
    couponDetails: state.couponDetails,
    couponValue: state.couponValue,
    isManualCouponCode: state.isManualCouponCode,
    isCouponValid: state.isCouponValid,
    isAcquisitionCoupon: state.isAcquisitionCoupon,
    isCouponExpired: state.isCouponExpired,
  }), [
    isLoadingCoupon,
    state.couponDetails,
    state.couponValue,
    state.isAcquisitionCoupon,
    state.isCouponExpired,
    state.isCouponValid,
    state.isManualCouponCode,
    state.websiteValidationResult,
  ]);

  const dialogCoupon = useMemo(() => ({
    dialogCouponValue: state.dialogCouponValue,
    dialogCouponDetails: state.dialogCouponDetails,
  }), [state.dialogCouponDetails, state.dialogCouponValue]);

  return [{
    coupon,
    dialogCoupon,
  }, {
    initializePromotionalOffers,
    updateUserCoupon,
    resetCouponStore,
  }];
};
